import React from 'react'
import { Link } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap'
import Meta from '../components/Meta'

const urls = {
  githubOrg: 'https://github.com/pixagan',
  laeyerz: 'https://laeyerz.com',
  laeyerzGithub: 'https://github.com/pixagan/laeyerz',
  laeyerzDocs: 'https://laeyerz.com',
  doqlets: '/doqlets',
  doqletsGithub: 'https://github.com/pixagan/doqlets',
  learn: '/learn',
  ragLearn: 'https://youtu.be/z-lXaIow93A?si=cFSI_PXk6uVT4j3h',
  about: '/#about',
  docs: '#todo-documentation-url',
  examplesGithub: 'https://github.com/pixagan/laeyerz-examples',
  contact: '/contact',
  privacy: '/privacypolicy',
  terms: '/termsandconditions',
}

const theme = {
  gradient: 'linear-gradient(90deg, #0C0854 0%, #6b0752 100%)',
  //gradient: 'linear-gradient(90deg, #800020 0%, #800020 100%)',
  ink: '#101225',
  muted: '#586174',
  purple: '#0C0854',
  pink: '#6b0752',
  soft: '#f7f7fb',
  cardShadow: '0 12px 34px rgba(28, 26, 52, 0.08)',
}

const navItems = [
  { label: 'Technologies', href: '#technologies' },
  { label: 'Laeyerz', href: '#laeyerz' },
  { label: 'Doqlets', href: '#doqlets' },
  { label: 'Examples', href: '#examples' },
  { label: 'Learn', to: urls.learn },
  { label: 'About', href: '#about' },
]

const laeyerzCapabilities = [
  'AI Agents',
  'Agent Teams',
  'Agentic Workflows',
  'Non-Agentic Workflows',
  'Tool Calling',
  'Shared State',
  'Graph-Based Execution',
  'Multi-Step AI Applications',
]

const doqletsCapabilities = [
  {
    title: 'RAG',
    text: 'Use documents and external knowledge to provide grounded information to LLMs through ingestion, chunking, embeddings, retrieval, search and question answering.',
    items: ['Document ingestion', 'Chunking', 'Embeddings', 'Retrieval', 'Search', 'Knowledge grounding'],
  },
  {
    title: 'LLM-Managed Wiki',
    text: 'Organize, summarize and maintain evolving knowledge pages while connecting related information for people, assistants and AI workflows.',
    items: ['Organize information', 'Summarize knowledge', 'Maintain pages', 'Connect related information'],
  },
]

const examples = [
  {
    name: 'Exam Grader Agent',
    category: 'Evaluation',
    description: 'Uses multiple grading strategies to evaluate student solutions consistently with logic checking, rubric grading and solution comparison.',
    href: 'https://github.com/pixagan/laeyerz-examples/tree/main/Examples/ExamGrader',
    github: 'https://github.com/pixagan/laeyerz-examples/tree/main/Examples/ExamGrader',
  },
  {
    name: 'Order Manager Agent',
    category: 'Operations',
    description: 'Processes a customer order through product extraction, availability checks, pricing, tax calculation and invoice generation.',
    href: 'https://github.com/pixagan/laeyerz-examples/tree/main/Examples/IncomeExpenseAgent',
    github: 'https://github.com/pixagan/laeyerz-examples/tree/main/Examples/IncomeExpenseAgent',
  },
  {
    name: 'Email Assistant (Coming Soon)',
    category: 'Productivity',
    description: 'Understands incoming requests and uses tools such as calendars and drafting systems to prepare useful responses.',
    href: '#todo-email-assistant-example-url',
    github: '#todo-email-assistant-github-url',
  },
  {
    name: 'Agentic RAG (Coming Soon)',
    category: 'Knowledge',
    description: 'An AI agent that chooses between multiple retrieval sources and retrieves additional information when needed.',
    href: '#todo-agentic-rag-example-url',
    github: '#todo-agentic-rag-github-url',
  },
  /*{
    name: 'Research Agent',
    category: 'Research',
    description: 'Searches information, evaluates results and assembles structured research from multiple steps and sources.',
    href: '#todo-research-agent-example-url',
    github: '#todo-research-agent-github-url',
  },
  {
    name: 'Knowledge Assistant',
    category: 'Doqlets',
    description: 'Uses Doqlets as a knowledge source for conversational assistance and agent-based tasks.',
    href: '#todo-knowledge-assistant-example-url',
    github: '#todo-knowledge-assistant-github-url',
  },*/
  
]

const architecture = [
  {
    title: 'AI Workflow',
    text: 'A predefined sequence of operations for well-defined tasks.',
  },
  {
    title: 'Agentic Workflow',
    text: 'A structured workflow where AI can make decisions within parts of the process.',
  },
  {
    title: 'AI Agent',
    text: 'A system that can reason, choose tools and dynamically decide what action to take next.',
  },
  {
    title: 'Agent Team',
    text: 'Multiple specialized agents collaborating to solve more complex tasks.',
  },
]

const learningTracks = [
  {
    title: 'Deep Learning Basics',
    description: 'Learn the foundations behind modern AI and large language models.',
    topics: ['Neural Networks', 'MLPs', 'CNNs', 'RNNs', 'Embeddings', 'Attention', 'Transformers', 'Large Language Models'],
  },
  {
    title: 'RAG',
    description: 'Learn how external knowledge can be connected to language models.',
    topics: ['Embeddings', 'Vector Databases', 'Document Chunking', 'Retrieval', 'Standard RAG', 'Hybrid RAG', 'Graph RAG', 'Agentic RAG'],
  },
  {
    title: 'AI Agents',
    description: 'Learn how LLMs become systems capable of reasoning, using tools and completing multi-step tasks.',
    topics: ['Agent Loops', 'ReAct', 'Tool Calling', 'Memory', 'Planning', 'Guardrails', 'Agentic Workflows', 'Agent Teams'],
  },
]

const openSourceLinks = [
  { label: 'Pixagan GitHub', href: urls.githubOrg },
  { label: 'Laeyerz GitHub', href: urls.laeyerzGithub },
  { label: 'Doqlets GitHub', href: urls.doqletsGithub },
  { label: 'Laeyerz Examples', href: urls.examplesGithub },
]

const isExternal = (href = '') => href.startsWith('http')
const isRoute = (href = '') => href.startsWith('/')

const CTAButton = ({ children, href, variant = 'primary', className = '' }) => {
  const styles = variant === 'primary'
    ? { background: theme.purple, borderColor: theme.purple, color: '#fff' }
    : variant === 'light'
      ? { background: '#fff', borderColor: '#fff', color: theme.purple }
      : { background: 'transparent', borderColor: theme.purple, color: theme.purple }

  if (isRoute(href)) {
    return (
      <Button as={Link} to={href} className={`px-4 py-2 ${className}`} style={{ ...styles, borderRadius: 999, fontWeight: 700 }}>
        {children}
      </Button>
    )
  }

  return (
    <Button
      href={href}
      target={isExternal(href) ? '_blank' : undefined}
      rel={isExternal(href) ? 'noreferrer' : undefined}
      className={`px-4 py-2 ${className}`}
      style={{ ...styles, borderRadius: 999, fontWeight: 700 }}
    >
      {children}
    </Button>
  )
}

const Section = ({ id, eyebrow, title, subtitle, children, dark = false }) => (
  <section id={id} style={{ padding: '40px 10px', background: dark ? theme.ink : '#f3ebf5', marginBottom:'solid', marginBottom:'1px' }}>
    <Container>
      {(eyebrow || title || subtitle) && (
        <div className='text-center mx-auto mb-5' style={{ maxWidth: 860 }}>
          {eyebrow && <div style={{ color: dark ? '#b9b4ff' : theme.pink, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', fontSize: 13 }}>{eyebrow}</div>}
          {title && <h2 className='fw-bold mt-2 mb-3' style={{ color: dark ? '#fff' : theme.ink, fontSize: 'clamp(30px, 5vw, 46px)' }}>{title}</h2>}
          {subtitle && <p style={{ color: dark ? '#d6d8e7' : theme.muted, fontSize: 18, lineHeight: 1.7 }}>{subtitle}</p>}
        </div>
      )}
      {children}
     
    </Container>
  </section>
)

const FeaturePill = ({ children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '10px 14px', borderRadius: 999, background: '#f0effa', color: theme.purple, fontWeight: 700, margin: 5 }}>
    {children}
  </span>
)

const InfoCard = ({ title, text, items }) => (
  <Card style={{ height: '100%', border: 'none', borderRadius: 22, boxShadow: theme.cardShadow }}>
    <Card.Body className='p-4'>
      <h4 className='fw-bold' style={{ color: theme.ink }}>{title}</h4>
      <p style={{ color: theme.muted, lineHeight: 1.65 }}>{text}</p>
      {items && (
        <div className='d-flex flex-wrap gap-2 mt-3'>
          {items.map((item) => <Badge key={item} bg='light' text='dark' style={{ border: '1px solid #e5e5ef', padding: '8px 10px' }}>{item}</Badge>)}
        </div>
      )}
    </Card.Body>
  </Card>
)

const ExampleCard = ({ example }) => (
  <Card style={{ height: '100%', border: '1px solid #ececf5', borderRadius: 22, boxShadow: '0 8px 24px rgba(16,18,37,0.05)' }}>
    <Card.Body className='p-4 d-flex flex-column'>
      <div className='mb-3'>
        <Badge bg='light' text='dark' style={{ border: '1px solid #e5e5ef' }}>{example.category}</Badge>
      </div>
      <h4 className='fw-bold' style={{ color: theme.ink }}>{example.name}</h4>
      <p style={{ color: theme.muted, lineHeight: 1.65, flex: 1 }}>{example.description}</p>
      <div className='d-flex gap-2 flex-wrap mt-2'>
        <CTAButton href={example.href} variant='outline'>View Example</CTAButton>
        <CTAButton href={example.github} variant='outline'>GitHub</CTAButton>
      </div>
    </Card.Body>
  </Card>
)

const LearningTrackCard = ({ track }) => (
  <Card style={{ height: '100%', border: 'none', borderRadius: 22, boxShadow: theme.cardShadow }}>
    <Card.Body className='p-4'>
      <h4 className='fw-bold' style={{ color: theme.purple }}>{track.title}</h4>
      <p style={{ color: theme.muted, lineHeight: 1.65 }}>{track.description}</p>
      <div className='d-flex flex-wrap gap-2'>
        {track.topics.map((topic) => <Badge key={topic} bg='light' text='dark' style={{ border: '1px solid #e5e5ef', padding: '8px 10px' }}>{topic}</Badge>)}
      </div>
    </Card.Body>
  </Card>
)

const Header = () => (
  <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #ececf5' }}>
    <Container>
      <div className='d-flex align-items-center justify-content-between py-3 gap-3 flex-wrap'>
        <a href='#top' className='d-flex align-items-center gap-2 text-decoration-none'>
        
       
          <span className='h3' style={{ fontWeight: 900, color: theme.ink }}>Pixagan.AI</span>
        </a>
       
        <nav className='d-none d-lg-flex align-items-center gap-4' style={{marginRight:'10px'}}>
          {navItems.map((item) => item.to ? (
            <Link key={item.label} to={item.to} style={{ color: theme.ink, textDecoration: 'none', fontWeight: 700 }}> | {item.label} </Link>
          ) : (
            <a key={item.label} href={item.href} style={{ color: theme.ink, textDecoration: 'none', fontWeight: 700 }}> | {item.label}  </a>
          ))} 
        </nav>
        <CTAButton href={urls.githubOrg} variant='outline'>GitHub</CTAButton>
      </div>
    </Container>
  </header>
)

const HeroSection = () => (
  <section
    id='top'
    style={{
      position: 'relative',
      isolation: 'isolate',
      background: theme.gradient,
      color: '#fff',
      padding: '110px 0 96px',
      overflow: 'hidden'
    }}
  >
    <style>{`
      .pixagan-hero-grid {
        position: absolute;
        inset: 0;
        z-index: -2;
        opacity: 0.24;
        background-image:
          linear-gradient(rgba(216, 213, 255, 0.2) 1px, transparent 1px),
          linear-gradient(90deg, rgba(216, 213, 255, 0.2) 1px, transparent 1px);
        background-size: 44px 44px;
        mask-image: linear-gradient(to bottom, black, transparent 90%);
      }

      .pixagan-hero-side-light {
        position: absolute;
        inset: 0;
        z-index: -3;
        pointer-events: none;
        background:
          radial-gradient(circle at 2% 52%, rgba(116, 158, 255, 0.2), transparent 30%),
          radial-gradient(circle at 98% 48%, rgba(164, 112, 255, 0.22), transparent 32%),
          radial-gradient(circle at 50% 0%, rgba(127, 86, 255, 0.18), transparent 42%);
      }

      .pixagan-hero-glow {
        position: absolute;
        z-index: -1;
        width: 520px;
        height: 520px;
        border-radius: 50%;
        filter: blur(28px);
        pointer-events: none;
      }

      .pixagan-hero-glow-one {
        top: -290px;
        left: 38%;
        background: rgba(127, 86, 255, 0.38);
      }

      .pixagan-hero-glow-two {
        right: -220px;
        bottom: -260px;
        background: rgba(255, 215, 0, 0.2);
      }

      .pixagan-eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        padding: 8px 13px;
        margin-bottom: 22px;
        border: 1px solid rgba(216, 213, 255, 0.26);
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.07);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
        color: #e8e6ff;
        font-size: 0.78rem;
        font-weight: 800;
        letter-spacing: 0.13em;
        text-transform: uppercase;
      }

      .pixagan-status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #FFD700;
        box-shadow: 0 0 0 5px rgba(255, 215, 0, 0.13), 0 0 18px #FFD700;
      }

      .pixagan-title {
        max-width: 780px;
        margin-bottom: 24px;
        color: #fff;
        font-size: clamp(46px, 6.4vw, 78px);
        font-weight: 800;
        line-height: 1.02;
        letter-spacing: -0.055em;
        text-shadow: 0 5px 18px rgba(0, 0, 0, 0.32), 0 0 34px rgba(160, 130, 255, 0.2);
      }

      .pixagan-title-accent {
        color: #FFD700;
        text-shadow: 0 4px 16px rgba(81, 53, 0, 0.42), 0 0 32px rgba(255, 215, 0, 0.3);
      }

      .pixagan-hero-card {
        position: relative;
        overflow: hidden;
        border: 1px solid rgba(216, 213, 255, 0.3) !important;
        border-radius: 24px !important;
        background: linear-gradient(145deg, rgba(255,255,255,0.18), rgba(255,255,255,0.07)) !important;
        box-shadow: 0 28px 80px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255,255,255,0.18);
        backdrop-filter: blur(16px);
      }

      .pixagan-hero-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 8%;
        width: 84%;
        height: 1px;
        background: linear-gradient(90deg, transparent, #FFD700, transparent);
        box-shadow: 0 0 18px rgba(255, 215, 0, 0.9);
      }

      .pixagan-card-label {
        color: #d8d5ff;
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }

      .pixagan-feature {
        padding: 11px 12px;
        border: 1px solid transparent;
        border-radius: 12px;
        transition: 0.2s ease;
      }

      .pixagan-feature:hover {
        border-color: rgba(255, 215, 0, 0.24);
        background: rgba(255, 255, 255, 0.06);
        transform: translateX(4px);
      }

      .pixagan-feature-dot {
        display: grid;
        flex: 0 0 auto;
        place-items: center;
        width: 26px;
        height: 26px;
        border: 1px solid rgba(255, 215, 0, 0.42);
        border-radius: 8px;
        background: rgba(255, 215, 0, 0.1);
      }

      .pixagan-feature-dot::after {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #FFD700;
        box-shadow: 0 0 12px rgba(255, 215, 0, 0.9);
      }

      @media (max-width: 991.98px) {
        .pixagan-hero-glow-one { left: -220px; }
      }
    `}</style>

    <div className='pixagan-hero-side-light' />
    <div className='pixagan-hero-grid' />
    <div className='pixagan-hero-glow pixagan-hero-glow-one' />
    <div className='pixagan-hero-glow pixagan-hero-glow-two' />

    <Container>
      <Row className='align-items-center g-5'>
        <Col lg={7}>
          <div className='pixagan-eyebrow'>
            <span className='pixagan-status-dot' />
            Pixagan.AI
          </div>

          <h1 className='pixagan-title'>
            Building <span className='pixagan-title-accent'>practical AI</span> technologies
          </h1>

          <p style={{ fontSize: 17, lineHeight: 1.75, color: '#d8d5ff', maxWidth: 700, marginBottom: 0 }}>
            From AI agents and agentic workflows to RAG systems and developer tools, Pixagan builds technologies that make advanced AI easier to develop, understand, and use.
          </p>

          <div className='d-flex gap-3 flex-wrap mt-4 pt-2'>
            <CTAButton href='#technologies' variant='light'>Explore Our Technologies</CTAButton>
            <CTAButton href={urls.githubOrg} variant='light'>View on GitHub</CTAButton>
          </div>
        </Col>

        <Col lg={5}>
          <Card className='pixagan-hero-card' style={{ color: '#fff' }}>
            <Card.Body className='p-4 p-md-5'>
              <div className='mb-3'>
                <span className='pixagan-card-label'>Technology stack</span>
              </div>

              <h3 className='fw-bold mb-4' style={{ color: '#fff', fontSize: 'clamp(25px, 3vw, 32px)', letterSpacing: '-0.03em' }}>
                Pixagan develops
              </h3>

              {[
                'Open-source AI frameworks',
                'AI agent and workflow technologies',
                'RAG and knowledge systems',
                'Practical AI apps and examples',
                'AI education and learning resources'
              ].map((item) => (
                <div key={item} className='pixagan-feature d-flex align-items-center gap-3'>
                  <span className='pixagan-feature-dot' />
                  <span style={{ color: '#f4f3ff', fontSize: 16, lineHeight: 1.35 }}>{item}</span>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </section>
)

const LaeyerzSection = () => (
  <Section
    id='laeyerz'
    eyebrow='Foundational technology'
    title='Laeyerz'
    subtitle='Build AI Agents, Agent Teams and Workflows in Python'
  >
    <Card style={{ border: 'none', borderRadius: 28, boxShadow: theme.cardShadow, overflow: 'hidden' }}>
      <Card.Body className='p-4 p-md-5'>
        <Row className='g-5 align-items-center'>
          <Col lg={6}>
            <p style={{ color: theme.muted, fontSize: 18, lineHeight: 1.75 }}>
              Laeyerz is an open-source Python framework for building AI agents, agent teams, agentic workflows and traditional AI workflows.
            </p>
            <p style={{ color: theme.muted, fontSize: 18, lineHeight: 1.75 }}>
              It gives developers a structured way to combine LLM calls, tools, application logic, state, reasoning, workflow nodes, agents and agent teams. Start with simple workflows and scale to agents and multi-agent systems using the same framework.
            </p>
            <div className='d-flex gap-3 flex-wrap mt-4'>
              <CTAButton href={urls.laeyerz}>Explore Laeyerz</CTAButton>
              <CTAButton href={urls.laeyerzGithub} variant='outline'>View Laeyerz on GitHub</CTAButton>
              <CTAButton href={urls.laeyerzDocs} variant='outline'>Documentation</CTAButton>
            </div>
          </Col>
          <Col lg={6}>
            <div className='mb-4'>
              {laeyerzCapabilities.map((capability) => <FeaturePill key={capability}>{capability}</FeaturePill>)}
            </div>
            <div style={{ background: theme.soft, borderRadius: 22, padding: 24 }}>
              <div className='d-flex align-items-center justify-content-between flex-wrap gap-2' style={{ color: theme.purple, fontWeight: 900, fontSize: 18 }}>
                {['Nodes', 'Tools', 'State', 'Agents', 'Teams'].map((item, index) => (
                  <React.Fragment key={item}>
                    <span>{item}</span>
                    {index < 4 && <span style={{ color: theme.pink }}>→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  </Section>
)

const DoqletsSection = () => (
  <Section
    id='doqlets'
    eyebrow='Knowledge technology'
    title='Doqlets'
    subtitle='Open-source RAG and AI-managed knowledge'
  >
    <Row className='g-4 align-items-stretch'>
      <Col lg={5}>
        <Card style={{ height: '100%', border: 'none', borderRadius: 28, background: theme.gradient, color: '#fff', boxShadow: theme.cardShadow }}>
          <Card.Body className='p-4 p-md-5'>
            <h3 className='fw-bold' style={{color:'white'}}>Doqlets is built on Laeyerz.</h3>
            <p style={{ color: '#f1efff', fontSize: 18, lineHeight: 1.75 }}>
              Doqlets is an open-source knowledge platform that turns collections of documents and knowledge sources into searchable, conversational and AI-managed knowledge systems.
            </p>
            <p style={{ color: '#d8d5ff', lineHeight: 1.7 }}>
              The same knowledge system can be used by RAG applications, AI agents, agentic workflows and knowledge assistants.
            </p>
            <div className='d-flex gap-3 flex-wrap mt-4'>
              <CTAButton href={urls.doqlets} variant='light'>Explore Doqlets</CTAButton>
              <CTAButton href={urls.doqletsGithub} variant='light'>View on GitHub</CTAButton>
              <CTAButton href={urls.ragLearn} variant='light'>Learn About RAG</CTAButton>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col lg={7}>
        <Row className='g-4'>
          {doqletsCapabilities.map((capability) => (
            <Col md={6} key={capability.title}>
              <InfoCard title={capability.title} text={capability.text} items={capability.items} />
            </Col>
          ))}
        </Row>
      </Col>
    </Row>
  </Section>
)

const ExamplesSection = () => (
  <Section
    id='examples'
    eyebrow='Applications and examples'
    title='Built with Laeyerz'
    subtitle='Explore practical AI agents and workflows that demonstrate reasoning, retrieval, tool use and multi-step execution.'
  >
    <Row className='g-4'>
      {examples.map((example) => (
        <Col md={6} lg={4} key={example.name}>
          <ExampleCard example={example} />
        </Col>
      ))}
    </Row>
    <div className='text-center mt-5'>
      <CTAButton href={urls.examplesGithub}>Explore all Laeyerz examples on GitHub</CTAButton>
    </div>
  </Section>
)

const ArchitectureSpectrumSection = () => (
  <Section
    id='architecture'
    dark
    eyebrow='AI architecture spectrum'
    title='Build as much intelligence as the task requires'
    subtitle='Pixagan technologies support a spectrum of AI architectures, from predictable workflows to adaptive agents and collaborative agent teams.'
  >
    <Row className='g-4'>
      {architecture.map((item, index) => (
        <Col md={6} lg={3} key={item.title}>
          <Card style={{ height: '100%', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 22, background: 'rgba(255,255,255,0.08)', color: '#fff' }}>
            <Card.Body className='p-4'>
              <div style={{ width: 44, height: 44, borderRadius: 999, background: '#fff', color: theme.purple, display: 'grid', placeItems: 'center', fontWeight: 900, marginBottom: 18 }}>{index + 1}</div>
              <h4 className='fw-bold'>{item.title}</h4>
              <p style={{ color: '#d6d8e7', lineHeight: 1.65 }}>{item.text}</p>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
    <div className='text-center mt-5' style={{ color: '#fff', fontWeight: 900, fontSize: 'clamp(20px, 4vw, 34px)' }}>
      AI Workflow → Agentic Workflow → AI Agent → Agent Team
    </div>
  </Section>
)

const LearnSection = () => (
  <Section
    id='learn'
    eyebrow='Pixagan Learn'
    title='Learn AI'
    subtitle='Understand how modern AI systems work. Pixagan creates practical learning modules for developers, students and technical learners who want to understand AI from foundational concepts through real applications.'
  >
    <Row className='g-4'>
      {learningTracks.map((track) => (
        <Col lg={4} key={track.title}>
          <LearningTrackCard track={track} />
        </Col>
      ))}
    </Row>
    <div className='text-center mt-5'>
      <CTAButton href={urls.learn}>Explore Learn AI</CTAButton>
    </div>
  </Section>
)

const OpenSourceSection = () => (
  <Section
    id='opensource'
    eyebrow='Open source'
    title='Built in the open'
    subtitle='Pixagan develops open-source technologies and practical reference implementations so developers can understand, modify and build upon the AI systems they use.'
  >
    <Row className='g-3 justify-content-center'>
      {openSourceLinks.map((link) => (
        <Col sm={6} lg={3} key={link.label}>
          <CTAButton href={link.href} variant='outline' className='w-100'>{link.label}</CTAButton>
        </Col>
      ))}
    </Row>
  </Section>
)

const AboutSection = () => (
  <Section id='about' eyebrow='Company' title='About Pixagan'>
    <Row className='justify-content-center'>
      <Col lg={9}>
        <Card style={{ border: 'none', borderRadius: 28, boxShadow: theme.cardShadow }}>
          <Card.Body className='p-4 p-md-5 text-center'>
            <p style={{ color: theme.muted, fontSize: 18, lineHeight: 1.75 }}>
              Pixagan.AI is an AI technology company building open-source frameworks, intelligent applications, knowledge systems, developer tools and educational resources for modern AI development.
            </p>
            <p style={{ color: theme.muted, fontSize: 18, lineHeight: 1.75 }}>
              Our work focuses on AI agents, agent teams, agentic workflows, RAG, AI knowledge systems, developer tools and AI education.
            </p>
            <h4 className='fw-bold my-4' style={{ color: theme.purple }}>Our goal is to make sophisticated AI systems simpler to build, inspect and understand.</h4>
            <div className='d-flex gap-3 justify-content-center flex-wrap'>
              <CTAButton href={urls.about}>About Pixagan</CTAButton>
              <CTAButton href={urls.githubOrg} variant='outline'>GitHub</CTAButton>
              <CTAButton href={urls.learn} variant='outline'>Learn AI</CTAButton>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  </Section>
)

const FinalCTASection = () => (
  <section style={{ padding: '78px 0', background: theme.gradient, color: '#fff' }}>
    <Container className='text-center'>
      <h2 className='fw-bold mb-4' style={{ fontSize: 'clamp(34px, 6vw, 58px)' }}>Build. Explore. Learn.</h2>
      <div style={{ color: '#f1efff', fontSize: 19, lineHeight: 1.8, marginBottom: 30 }}>
        <div>Build AI systems with Laeyerz.</div>
        <div>Create intelligent knowledge systems with Doqlets.</div>
        <div>Learn how modern AI works with Pixagan Learn.</div>
      </div>
      <div className='d-flex gap-3 justify-content-center flex-wrap'>
        <CTAButton href={urls.laeyerz} variant='light'>Explore Laeyerz</CTAButton>
        <CTAButton href={urls.githubOrg} variant='light'>View GitHub</CTAButton>
        <CTAButton href={urls.learn} variant='light'>Learn AI</CTAButton>
      </div>
    </Container>
  </section>
)

const FooterColumn = ({ title, links }) => (
  <Col sm={6} lg={3}>
    <h5 className='fw-bold mb-3' style={{ color: '#fff' }}>{title}</h5>
    {links.map((link) => isRoute(link.href) ? (
      <Link key={link.label} to={link.href} className='d-block mb-2 text-decoration-none' style={{ color: '#cfd2e5' }}>{link.label}</Link>
    ) : (
      <a key={link.label} href={link.href} target={isExternal(link.href) ? '_blank' : undefined} rel={isExternal(link.href) ? 'noreferrer' : undefined} className='d-block mb-2 text-decoration-none' style={{ color: '#cfd2e5' }}>{link.label}</a>
    ))}
  </Col>
)

const Footer = () => (
  <footer style={{ background: '#090a18', color: '#cfd2e5', padding: '56px 0 28px' }}>
    <Container>
      <Row className='g-4'>
        <FooterColumn title='Pixagan' links={[{ label: 'About', href: urls.about }, { label: 'Technologies', href: '#technologies' }, { label: 'Learn', href: urls.learn }]} />
        <FooterColumn title='Technologies' links={[{ label: 'Laeyerz', href: urls.laeyerz }, { label: 'Doqlets', href: urls.doqlets }, { label: 'Examples', href: '#examples' }]} />
        <FooterColumn title='Developer' links={[{ label: 'GitHub', href: urls.githubOrg }, { label: 'Documentation', href: urls.docs }]} />
        <FooterColumn title='Additional' links={[{ label: 'Contact', href: urls.contact }, { label: 'Privacy', href: urls.privacy }, { label: 'Terms', href: urls.terms }]} />
      </Row>
      <div className='mt-5 pt-4 d-flex justify-content-between flex-wrap gap-3' style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        <strong style={{ color: '#fff' }}>Pixagan.AI</strong>
        <span>biz@pixagan.com</span>
        <span>© Pixagan.AI</span>
      </div>
    </Container>
  </footer>
)

export const LandingScreen = () => {
  return (
    <div style={{ fontFamily: 'Inter, Roboto, Open Sans, Arial, sans-serif', background: theme.soft, marginTop: 0, paddingTop: 0 }}>
      <Meta
        title='Pixagan.AI'
        description='Pixagan develops open-source AI frameworks, agent and workflow technologies, RAG systems, practical AI applications and AI learning resources.'
      />
      
      <HeroSection />
      <Header />
      
      <Section
        id='technologies'
        eyebrow='Technology ecosystem'
        title='Technologies from Pixagan'
        subtitle='Pixagan builds reusable AI foundations first, then develops knowledge systems, examples and learning resources around them.'
        
      >
        <Row className='g-4'>
          <Col md={6}>
            <InfoCard
              title='Laeyerz'
              text='Open-source framework for AI agents, agent teams, agentic workflows and traditional AI workflows in Python.'
              items={['Agents', 'Workflows', 'Tools', 'State', 'Teams']}
            />
          </Col>
          <Col md={6}>
            <InfoCard
              title='Doqlets'
              text='Open-source RAG and AI-managed knowledge platform built on Laeyerz for searchable and conversational knowledge systems.'
              items={['RAG', 'Knowledge', 'Wiki', 'Retrieval', 'Assistants']}
            />
          </Col>
        </Row>
      </Section>
      <LaeyerzSection />
      <DoqletsSection />
      <ExamplesSection />
      {/*<ArchitectureSpectrumSection />*/}
      <LearnSection />
      <OpenSourceSection />
      <AboutSection />
      {/*<FinalCTASection />*/}
      {/*<Footer />*/}
    </div>
  )
}

export default LandingScreen
